import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import { FormattedMessage, useIntl } from 'react-intl';
import { DropdownMenuHeader, DropdownMenuItem } from '@bibliocommons/base-dropdown';
import { eventShape } from 'app/shapes/events';
import { selectAppConfig } from 'app/selectors/AppSelector';
import { selectLocationEntities, selectPlaceEntities } from 'app/selectors/EntitiesSelector';
import { formatTitle, formatDescription, formatDate, formatAddress, isAllDayEvent } from 'app/helpers/events/utils';
import PropTypes from 'prop-types';

const GOOGLE_CALENDER = 'https://www.google.com/calendar/render?action=TEMPLATE&';
const OUTLOOK_CALENDAR = 'https://outlook.live.com/calendar/0/action/compose?';
const ONLINE_EVENT = 'Online event';

function toQueryString(params) {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

export default function AddToCalendar({ event, trackAddToCalendarClick }) {
  const intl = useIntl();

  const appConfig = useSelector(selectAppConfig);
  const locationEntities = useSelector(selectLocationEntities);
  const places = useSelector(selectPlaceEntities);

  const appVersion = appConfig?.get('coreVersion');
  const baseURL = appConfig.get('baseURL');
  const filename = `${appConfig.get('libraryDomain')}.ics`;

  const eventTitle = formatTitle(event.getIn(['definition', 'title']).trim());
  const eventId = event.get('id');
  const eventDescription = formatDescription(event.getIn(['definition', 'description']).trim(), 240);
  const isAllDay = isAllDayEvent(event.getIn(['definition', 'start']), event.getIn(['definition', 'end']));
  const eventStart = isAllDay
    ? moment(event.getIn(['definition', 'start'])).format('YYYYMMDD')
    : formatDate(event.get('indexStart'), 'YYYYMMDD[T]HHmmss[Z]');
  const eventEnd = isAllDay
    ? moment(event.getIn(['definition', 'end']))
        .add(1, 'day')
        .format('YYYYMMDD')
    : formatDate(event.get('indexEnd'), 'YYYYMMDD[T]HHmmss[Z]');
  const isCancelled = event.getIn(['definition', 'isCancelled']);
  const eventLink = `${baseURL}/events/${eventId}`;

  const isVirtual = event.getIn(['definition', 'isVirtual']);
  const location = event.getIn(['definition', 'branchLocationId'])
    ? locationEntities.get(event.getIn(['definition', 'branchLocationId']))
    : places.get(event.getIn(['definition', 'nonBranchLocationId']));
  const eventLocation = isVirtual ? ONLINE_EVENT : formatAddress(location);

  const appleOutlookText = intl.formatMessage({ id: 'apple_and_outlook' });
  const googleCalendarText = intl.formatMessage({ id: 'google_calendar' });
  const outlookText = intl.formatMessage({ id: 'outlook' });

  function createCalendarFile() {
    trackAddToCalendarClick({
      linkText: appleOutlookText,
      eventId
    });

    const calendar = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      `PRODID:-//BiblioCommons//NONSGML BiblioEvents ${appVersion}//EN`,
      `${isCancelled ? 'METHOD:CANCELLED' : ''}`,
      'BEGIN:VEVENT',
      `UID:${eventId}@${baseURL}`,
      `SUMMARY:${eventTitle}`,
      `DTSTAMP:${formatDate(event.get('indexStart'), 'YYYYMMDD[T]HHmmss[Z]')}`,
      `DTSTART${isAllDay ? ';VALUE=DATE' : ''}:${eventStart}`,
      `DTEND${isAllDay ? ';VALUE=DATE' : ''}:${eventEnd}`,
      `${isCancelled ? 'STATUS:CANCELLED' : ''}`,
      `DESCRIPTION:${eventDescription}...\\n\\n View all details for this event:\\n ${eventLink}`,
      `LOCATION:${eventLocation}`,
      `URL:${eventLink}`,
      'CLASS:PUBLIC',
      'TRANSP:TRANSPARENT',
      'END:VEVENT',
      'BEGIN:VALARM',
      'TRIGGER:-PT1440M',
      'ACTION:DISPLAY',
      `DESCRIPTION:Event Reminder - ${eventTitle}`,
      'END:VALARM',
      'END:VCALENDAR'
    ]
      .filter(item => item)
      .join('\n');

    const file = new File([calendar], filename, { type: 'text/calendar' });
    const url = URL.createObjectURL(file);
    window.open(url);
  }

  function openGoogleCalendar() {
    trackAddToCalendarClick({
      linkText: googleCalendarText,
      eventId
    });

    const googleCalendarDetails = `${eventDescription}\n\nFor more information go here:\n${eventLink}`;
    const googleCalendarQuery = toQueryString({
      text: eventTitle,
      details: googleCalendarDetails,
      dates: `${eventStart}/${eventEnd}`,
      location: eventLocation
    });
    window.open(`${GOOGLE_CALENDER}${googleCalendarQuery}`);
  }

  function openOutlookCalendar() {
    trackAddToCalendarClick({
      linkText: outlookText,
      eventId
    });

    const outlookCalendarBody = `${eventDescription}\n\nFor more information go here:\n${eventLink}`;
    const outlookCalendarQuery = toQueryString({
      allday: isAllDay,
      subject: eventTitle,
      body: outlookCalendarBody,
      startdt: eventStart,
      enddt: eventEnd,
      location: eventLocation
    });
    window.open(`${OUTLOOK_CALENDAR}${outlookCalendarQuery}`);
  }

  return (
    <>
      <DropdownMenuHeader>
        <FormattedMessage id="add_to_calendar" />
      </DropdownMenuHeader>
      <DropdownMenuItem
        item={Immutable.Map({ label: appleOutlookText })}
        dataKey="calender_download_link"
        handleClick={createCalendarFile}
      />
      <DropdownMenuItem
        item={Immutable.Map({ label: googleCalendarText })}
        dataKey="google_calendar_link"
        handleClick={openGoogleCalendar}
      />
      <DropdownMenuItem
        item={Immutable.Map({ label: outlookText })}
        dataKey="outlook_calendar_link"
        handleClick={openOutlookCalendar}
      />
    </>
  );
}

AddToCalendar.propTypes = {
  event: eventShape.isRequired,
  trackAddToCalendarClick: PropTypes.func.isRequired
};
